import React, { useState, useEffect } from "react";
import { useCheckout } from "./CheckoutContext";
import { Box } from "@mui/material";
import CheckoutForm from "./CheckoutForm";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "./contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";

const Checkout = ({ userCountry }) => {
  const { state: checkoutState, dispatch } = useCheckout();
  const { step, shippingDetails, cartItems } = checkoutState;
  const { isAuthenticated } = useAuth0();
  const { user } = useAuth();
  const [shippingErrors, setShippingErrors] = useState({});

  // Get the first shipping address (or empty object if none)
  const currentShippingDetails = shippingDetails[0] || {};

  useEffect(() => {
    dispatch({ type: "SET_STEP", payload: 3 });
    window.scrollTo(0, 0);
  }, [dispatch]);

  const handleNextStep = () => {
    let newStep = step + 1;
    if (isAuthenticated && step === 1) {
      newStep = 3;
    }
    dispatch({ type: "SET_STEP", payload: newStep });
    window.history.pushState({ step: newStep }, "");
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;

    const updatedDetails = {
      ...currentShippingDetails,
      [name]: value,
      id: currentShippingDetails.id || uuidv4(),
      isDefault: true,
    };

    // Clear the specific field error when user enters a valid value
    setShippingErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });

    const existingAddressIndex = shippingDetails.findIndex(
      (addr) => addr.id === updatedDetails.id
    );

    if (existingAddressIndex >= 0) {
      dispatch({ type: "EDIT_SHIPPING_ADDRESS", payload: updatedDetails });
    } else {
      dispatch({ type: "ADD_SHIPPING_ADDRESS", payload: updatedDetails });
    }
  };

  // Auto-fill name from user if available
  useEffect(() => {
    if (
      user?.name &&
      (!currentShippingDetails.firstName || !currentShippingDetails.lastName)
    ) {
      const nameParts = user.name.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ") || "";

      const userDetails = {
        ...currentShippingDetails,
        id: currentShippingDetails.id || uuidv4(),
        firstName,
        lastName,
        email: user?.email || currentShippingDetails.email || "",
        isDefault: true,
      };

      dispatch({
        type: "ADD_SHIPPING_ADDRESS",
        payload: userDetails,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  const subtotal = cartItems.reduce((sum, item) => {
    if (!item.price) return sum;
    const priceStr = String(item.price).replace(/[^0-9.-]+/g, "");
    return sum + (parseFloat(priceStr) || 0) * (item.quantity || 1);
  }, 0);

  const getShippingCost = (country) => {
    /* 
    if (!country) return 0;
    const c = country.toLowerCase();
    if (c === "india" || c === "in") return 100;
    if (c === "canada" || c === "ca") return 8.95;
    
    // Euro countries
    const euroCountries = ["germany", "france", "italy", "spain", "netherlands", "belgium", "austria", "portugal", "ireland", "greece", "finland", "estonia", "latvia", "lithuania", "slovakia", "slovenia", "luxembourg", "malta", "cyprus"];
    if (euroCountries.includes(c)) return 5.95;
    
    // UK
    if (c === "united kingdom" || c === "uk" || c === "gb") return 4.95;
    
    // US and default dollar countries
    return 6.95;
    */
    return 0; // Universal Free Shipping as of now
  };

  const shippingCost = getShippingCost(currentShippingDetails.country);
  const tax = subtotal * 0.1;
  const grandTotal = subtotal + shippingCost + tax;

  return (
    <Box
      sx={{
        maxWidth: { xs: "100%", lg: 1200 },
        mx: "auto",
        mt: { xs: "60px", lg: "80px" },
        mb: 4,
      }}
    >
      <CheckoutForm
        cartItems={cartItems}
        shippingDetails={currentShippingDetails}
        subtotal={subtotal}
        shippingCost={shippingCost}
        tax={tax}
        grandTotal={grandTotal}
        handleShippingChange={handleShippingChange}
        handleNextStep={handleNextStep}
        shippingErrors={shippingErrors}
        setShippingErrors={setShippingErrors}
        userCountry={userCountry}
      />
    </Box>
  );
};

export default Checkout;
